import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { getFunctions, httpsCallable } from "firebase/functions";
import { collection, query, where, orderBy, onSnapshot, limit as firestoreLimit } from "firebase/firestore";
import { db } from "../firebase";
import LikeButton from "./LikeButton";

const CommentSection = ({ blogId, blogTitle }) => {
  const [comments, setComments] = useState([]);
  const [topLevelComments, setTopLevelComments] = useState([]);
  const [sortBy, setSortBy] = useState("newest");
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const commentsPerPage = 10;

  useEffect(() => {
    // Real-time listener for comments
    const commentsQuery = query(
      collection(db, "Comments"),
      where("blogId", "==", blogId),
      where("isDeleted", "==", false)
    );

    const unsubscribe = onSnapshot(commentsQuery, (snapshot) => {
      const commentsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      setComments(commentsData);

      // Filter top-level comments (no parent)
      const topLevel = commentsData.filter(c => !c.parentCommentId);

      // Sort based on selected option
      let sorted = [...topLevel];
      if (sortBy === "newest") {
        sorted.sort((a, b) => b.createdAt - a.createdAt);
      } else if (sortBy === "oldest") {
        sorted.sort((a, b) => a.createdAt - b.createdAt);
      } else if (sortBy === "most-liked") {
        sorted.sort((a, b) => (b.likesCount || 0) - (a.likesCount || 0));
      }

      setTopLevelComments(sorted);
      setLoading(false);
    }, (error) => {
      console.error("Error fetching comments:", error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [blogId, sortBy]);

  // Get replies for a comment
  const getReplies = (parentId) => {
    return comments
      .filter(c => c.parentCommentId === parentId)
      .sort((a, b) => a.createdAt - b.createdAt); // Replies always oldest first
  };

  // Paginated comments
  const paginatedComments = topLevelComments.slice(0, page * commentsPerPage);
  const hasMore = topLevelComments.length > paginatedComments.length;

  return (
    <div className="w-full">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-2xl font-bold text-white flex items-center gap-2">
          <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
          </svg>
          Comments ({topLevelComments.length})
        </h3>

        {/* Sort Options */}
        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          className="px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 cursor-pointer"
        >
          <option value="newest">Newest First</option>
          <option value="oldest">Oldest First</option>
          <option value="most-liked">Most Liked</option>
        </select>
      </div>

      {/* Comment Form */}
      <CommentForm blogId={blogId} parentCommentId={null} />

      {/* Comments List */}
      <div className="mt-8 space-y-4">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-8 h-8 border-4 border-purple-500 border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : paginatedComments.length === 0 ? (
          <div className="text-center py-12">
            <svg className="w-16 h-16 mx-auto text-gray-600 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
            <p className="text-gray-400 text-lg">No comments yet. Be the first to share your thoughts!</p>
          </div>
        ) : (
          <AnimatePresence>
            {paginatedComments.map((comment, index) => (
              <CommentItem
                key={comment.id}
                comment={comment}
                replies={getReplies(comment.id)}
                allComments={comments}
                depth={0}
              />
            ))}
          </AnimatePresence>
        )}

        {/* Load More */}
        {hasMore && (
          <div className="flex justify-center pt-6">
            <button
              onClick={() => setPage(page + 1)}
              className="px-6 py-3 bg-white/5 border border-white/10 text-white rounded-lg hover:bg-white/10 hover:border-purple-500/50 transition-all"
            >
              Load More Comments
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// Individual Comment Item (Recursive for nested replies)
const CommentItem = ({ comment, replies, allComments, depth = 0 }) => {
  const [showReplyForm, setShowReplyForm] = useState(false);
  const [showReplies, setShowReplies] = useState(true);
  const maxVisualDepth = 3;

  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);

    if (diffInSeconds < 60) return "just now";
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`;

    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: date.getFullYear() !== now.getFullYear() ? "numeric" : undefined
    });
  };

  const getRepliesRecursive = (parentId) => {
    return allComments
      .filter(c => c.parentCommentId === parentId)
      .sort((a, b) => a.createdAt - b.createdAt);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      className={`${depth > 0 ? "ml-8 md:ml-12" : ""} ${depth >= maxVisualDepth ? "ml-0" : ""}`}
    >
      <div className="bg-white/5 border border-white/10 rounded-lg p-4 hover:border-white/20 transition-all">
        {/* Comment Header */}
        <div className="flex items-start gap-3 mb-3">
          {/* Avatar */}
          <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center flex-shrink-0">
            <span className="text-white font-bold text-sm">
              {comment.authorName.charAt(0).toUpperCase()}
            </span>
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold text-white">{comment.authorName}</span>
              <span className="text-gray-500 text-sm">•</span>
              <span className="text-gray-500 text-sm">{formatDate(comment.createdAt)}</span>
            </div>
          </div>
        </div>

        {/* Comment Content */}
        <div className="ml-13">
          <p className="text-gray-300 whitespace-pre-wrap break-words mb-4">
            {comment.content}
          </p>

          {/* Comment Actions */}
          <div className="flex items-center gap-4 flex-wrap">
            {/* Like Button (smaller version) */}
            <div className="scale-75 origin-left">
              <LikeButton
                targetType="comment"
                targetId={comment.id}
                initialLikesCount={comment.likesCount || 0}
              />
            </div>

            {/* Reply Button */}
            <button
              onClick={() => setShowReplyForm(!showReplyForm)}
              className="flex items-center gap-2 text-gray-400 hover:text-purple-400 transition-colors text-sm"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
              </svg>
              Reply
            </button>

            {/* Show/Hide Replies */}
            {comment.replyCount > 0 && (
              <button
                onClick={() => setShowReplies(!showReplies)}
                className="flex items-center gap-2 text-gray-400 hover:text-purple-400 transition-colors text-sm"
              >
                <svg className={`w-4 h-4 transition-transform ${showReplies ? "rotate-180" : ""}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
                {comment.replyCount} {comment.replyCount === 1 ? "Reply" : "Replies"}
              </button>
            )}
          </div>
        </div>

        {/* Reply Form */}
        {showReplyForm && (
          <div className="ml-13 mt-4">
            <CommentForm
              blogId={comment.blogId}
              parentCommentId={comment.id}
              onSuccess={() => setShowReplyForm(false)}
              isReply
            />
          </div>
        )}
      </div>

      {/* Nested Replies */}
      {showReplies && replies.length > 0 && (
        <div className="mt-4 space-y-4">
          {replies.map(reply => (
            <CommentItem
              key={reply.id}
              comment={reply}
              replies={getRepliesRecursive(reply.id)}
              allComments={allComments}
              depth={depth + 1}
            />
          ))}
        </div>
      )}
    </motion.div>
  );
};

// Comment Form Component
const CommentForm = ({ blogId, parentCommentId = null, onSuccess, isReply = false }) => {
  const [userName, setUserName] = useState("");
  const [userEmail, setUserEmail] = useState("");
  const [content, setContent] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [charCount, setCharCount] = useState(0);
  const maxChars = 1000;

  useEffect(() => {
    // Load saved user info
    const savedName = localStorage.getItem("cosmovex_user_name");
    const savedEmail = localStorage.getItem("cosmovex_user_email");
    if (savedName) setUserName(savedName);
    if (savedEmail) setUserEmail(savedEmail);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (content.length < 10) {
      alert("Comment must be at least 10 characters");
      return;
    }

    if (content.length > maxChars) {
      alert(`Comment must be less than ${maxChars} characters`);
      return;
    }

    setIsSubmitting(true);

    try {
      const functions = getFunctions();
      const addComment = httpsCallable(functions, "addComment");

      await addComment({
        blogId,
        userName: userName.trim(),
        userEmail: userEmail.trim(),
        content: content.trim(),
        parentCommentId
      });

      // Save user info
      localStorage.setItem("cosmovex_user_name", userName.trim());
      localStorage.setItem("cosmovex_user_email", userEmail.trim());

      // Reset form
      setContent("");
      setCharCount(0);

      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      console.error("Error adding comment:", error);
      alert(error.message || "Failed to add comment. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="bg-white/5 border border-white/10 rounded-lg p-4">
      <h4 className="text-lg font-semibold text-white mb-4">
        {isReply ? "Write a Reply" : "Leave a Comment"}
      </h4>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
        <input
          type="text"
          value={userName}
          onChange={(e) => setUserName(e.target.value)}
          placeholder="Your Name *"
          className="px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20"
          required
          minLength={2}
          maxLength={50}
        />
        <input
          type="email"
          value={userEmail}
          onChange={(e) => setUserEmail(e.target.value)}
          placeholder="your@email.com *"
          className="px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20"
          required
        />
      </div>

      <textarea
        value={content}
        onChange={(e) => {
          setContent(e.target.value);
          setCharCount(e.target.value.length);
        }}
        placeholder="Share your thoughts..."
        className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 resize-none"
        rows={isReply ? 3 : 4}
        required
        minLength={10}
        maxLength={maxChars}
      />

      <div className="flex items-center justify-between mt-3">
        <span className={`text-sm ${charCount > maxChars ? "text-red-400" : charCount > maxChars * 0.9 ? "text-yellow-400" : "text-gray-500"}`}>
          {charCount}/{maxChars} characters
        </span>

        <div className="flex items-center gap-2">
          {isReply && onSuccess && (
            <button
              type="button"
              onClick={onSuccess}
              className="px-4 py-2 bg-white/5 border border-white/10 text-gray-300 rounded-lg hover:bg-white/10 transition-colors"
            >
              Cancel
            </button>
          )}
          <button
            type="submit"
            disabled={isSubmitting || charCount < 10 || charCount > maxChars}
            className={`px-6 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-semibold rounded-lg transition-all ${
              isSubmitting || charCount < 10 || charCount > maxChars
                ? "opacity-50 cursor-not-allowed"
                : "hover:shadow-lg hover:shadow-purple-500/50"
            }`}
          >
            {isSubmitting ? "Posting..." : isReply ? "Post Reply" : "Post Comment"}
          </button>
        </div>
      </div>

      <p className="text-xs text-gray-500 mt-3">
        Your email will not be published. Required fields are marked *
      </p>
    </form>
  );
};

export default CommentSection;
